import React from 'react';
import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import { CustomUnitFields, UnitSystem } from 'providers/api';
import { BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { distanceMetricToImperial, isCustomUnitField, volumeMetricToImperial } from 'utils/unitConversions';
import {
  bandStepPoints,
  fillHeightPoints,
  groupSlicesByPour,
  RiseRateSeries
} from 'modules/project/StopeFillPage/Three/riseRateSeries';
import { PIPE_COLOURS } from '../../MineModel/GConstants';

export const RISE_RATE_CHART_ID = 'stope-rise-rate-chart';

// Fill height is deliberately not one of the pour colours - it is a different quantity on its own axis,
// so it reads as an annotation rather than as another pour.
const FILL_HEIGHT_COLOUR = '#9e9e9e';

const TARGET_X_TICKS = 8;

// A slice in a wide stope holds hundreds of cubic metres and wants no decimals; one in a narrow lobe can
// hold well under one, where rounding would print a misleading "0".
const formatSliceVolume = (value: number): string => (value >= 10 ? String(Math.round(value)) : value.toFixed(1));

/**
 * A round axis step near `span / TARGET_X_TICKS`, so the time axis reads 0/10/20/... rather than the
 * 0/12.9/25.9/... that falls out of dividing an arbitrary duration into equal parts.
 */
const niceStep = (span: number): number => {
  if (!(span > 0)) return 1;
  const raw = span / TARGET_X_TICKS;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10]
    .map((multiple) => multiple * magnitude)
    .find((candidate) => candidate >= raw);
  return step ?? magnitude * 10;
};

export interface RiseRateChartProps {
  series: RiseRateSeries;
  /** Display name per pour entity id, so the legend reads like the volume table. */
  pourNames: Map<string, string>;
  unitSystemPreference?: UnitSystem;
  customUnitFields?: CustomUnitFields[];
  height?: number;
  /**
   * `dark` for the app's canvas, `light` for a capture bound for the white page of a note. Getting
   * this wrong is invisible on screen and fatal in the PDF: the dark palette's label colour is
   * near-white, which vanishes on paper.
   */
  mode?: 'dark' | 'light';
  /** Distinct per mounted instance, so ApexCharts.exec targets the right chart when capturing. */
  chartId?: string;
  /** Hides the captions, which belong beside the on-screen panel rather than in a captured image. */
  hideCaptions?: boolean;
}

// Fixed rather than theme-derived: a capture goes onto white paper, where the dark theme's near-white
// label colour would be invisible. Mirrors the HGL chart, which also renders light for export.
const LIGHT_AXIS_COLOUR = 'rgba(0, 0, 0, 0.72)';
const LIGHT_GRID_COLOUR = 'rgba(0, 0, 0, 0.14)';

const RiseRateChart = ({
  series, pourNames, unitSystemPreference, customUnitFields, height = 240,
  mode = 'dark', chartId = RISE_RATE_CHART_ID, hideCaptions = false,
}: RiseRateChartProps) => {
  // ApexCharts defaults to a light theme: near-black label text and #e0e0e0 gridlines. On this app's
  // dark paper that makes every label invisible and every gridline glare, so the palette drives both.
  const theme = useTheme();
  const axisColour = mode === 'light' ? LIGHT_AXIS_COLOUR : theme.palette.text.secondary;
  const gridColour = mode === 'light' ? LIGHT_GRID_COLOUR : theme.palette.divider;

  // Same unit gates the volume table uses: rise rates and heights are PipeLength quantities, slice
  // volumes are PourVolume. They are independent custom-unit fields, so they resolve separately - a
  // project can be imperial for one and metric for the other.
  const isImperial = unitSystemPreference !== undefined
    && isCustomUnitField(unitSystemPreference, customUnitFields ?? [], unitLabels[BasicDisplayUnits.Distance].customUnitField);
  const isImperialVolume = unitSystemPreference !== undefined
    && isCustomUnitField(unitSystemPreference, customUnitFields ?? [], unitLabels[BasicDisplayUnits.Volume].customUnitField);
  const toLength = (value: number) => (isImperial ? distanceMetricToImperial(value) : value);
  const toVolume = (value: number) => (isImperialVolume ? volumeMetricToImperial(value) : value);
  const lengthUnit = isImperial ? 'ft' : 'm';
  const volumeUnit = isImperialVolume ? 'ft\u00B3' : 'm\u00B3';

  // Only the bands that have a rate to draw. A pour with no flow rate occupies no time, so plotting it
  // at zero would read as a measured standstill; it still shows in the fill-height series.
  const bands = React.useMemo(
    () => groupSlicesByPour(series.slices).filter((band) => band.peakRiseRate !== undefined),
    [series],
  );

  // bandStepPoints plots one point per RATED slice (plus a closing point), so a tooltip's dataPointIndex
  // indexes this list rather than band.slices - hence the same filter here.
  const ratedSlicesByBand = React.useMemo(
    () => bands.map((band) => band.slices.filter((slice) => slice.riseRate !== undefined)),
    [bands],
  );

  const chartSeries = React.useMemo(() => {
    // One series per pour, so each takes the colour its 3D shell and table row already use.
    const rateSeries = bands.map((band) => ({
      // Upstream already falls back to "Pour N" when a pour has no reference, so this rarely fires.
      name: pourNames.get(band.pourEntityId) ?? `Pour ${band.pourIndex + 1}`,
      type: 'line',
      data: bandStepPoints(band).map((point) => ({ x: point.hours, y: toLength(point.riseRate) })),
    }));

    return [
      ...rateSeries,
      {
        name: i18n._('Fill height'),
        type: 'line',
        data: fillHeightPoints(series).map((point) => ({ x: point.hours, y: toLength(point.height) })),
      },
    ];
  }, [bands, series, pourNames, isImperial]);

  const options: ApexOptions = React.useMemo(() => {
    const labelStyle = { fontSize: '10px', colors: axisColour };
    const titleStyle = { fontSize: '10px', color: axisColour, fontWeight: 400 };
    const rateAxisCount = chartSeries.length - 1;
    const xStep = niceStep(series.totalHours);
    const axisMax = Math.max(xStep, Math.ceil(series.totalHours / xStep) * xStep);

    return {
      chart: {
        id: chartId,
        type: 'line',
        animations: { enabled: false },
        toolbar: { show: false },
        zoom: { enabled: false },
        background: 'transparent',
        foreColor: axisColour,
      },
      theme: { mode },
      // The height series is the last entry and is drawn smooth on its own axis; every rate series
      // before it is a stepline, because a slice's rate is constant across that slice by construction.
      stroke: {
        curve: [...Array.from({ length: rateAxisCount }, () => 'stepline' as const), 'smooth' as const],
        width: [...Array.from({ length: rateAxisCount }, () => 2), 2],
        dashArray: [...Array.from({ length: rateAxisCount }, () => 0), 5],
      },
      // The palette wraps, so a stope with more pours than PIPE_COLOURS holds still gets a colour.
      colors: [
        ...bands.map((band) => PIPE_COLOURS[band.pourIndex % PIPE_COLOURS.length]),
        FILL_HEIGHT_COLOUR,
      ],
      markers: { size: 0 },
      // apexcharts 5 dropped legend.markers width/height in favour of size.
      //
      // clusterGroupedSeries defaults to TRUE in apexcharts 5, which wraps the entries in group divs
      // that its own stylesheet lays out with `flex-direction: column-reverse` - a tall reversed stack
      // instead of a row. Turning it off restores the plain wrapping row that `position: bottom` implies.
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '10px',
        markers: { size: 4 },
        itemMargin: { horizontal: 8, vertical: 0 },
        labels: { colors: axisColour },
        clusterGroupedSeries: false,
      },
      grid: {
        padding: { left: 6, right: 6 },
        borderColor: gridColour,
      },
      xaxis: {
        type: 'numeric',
        title: { text: i18n._('Elapsed pouring time (h)'), style: titleStyle },
        // The step is round, so a whole number of hours prints without a decimal point.
        labels: {
          formatter: (value: string) => {
            const hours = Number(value);
            return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
          },
          style: labelStyle,
        },
        min: 0,
        max: axisMax,
        tickAmount: Math.max(1, Math.round(axisMax / xStep)),
        axisBorder: { color: gridColour },
        axisTicks: { color: gridColour },
      },
      yaxis: [
        ...Array.from({ length: rateAxisCount }, (unused, index) => ({
          seriesName: chartSeries[0].name,
          show: index === 0,
          title: { text: `${i18n._('Rise rate')} (${lengthUnit}/h)`, style: titleStyle },
          labels: { formatter: (value: number) => value.toFixed(2), style: labelStyle },
          axisBorder: { show: index === 0, color: gridColour },
          min: 0,
        })),
        {
          seriesName: i18n._('Fill height'),
          opposite: true,
          title: { text: `${i18n._('Fill height')} (${lengthUnit})`, style: titleStyle },
          labels: { formatter: (value: number) => value.toFixed(1), style: labelStyle },
          axisBorder: { show: true, color: gridColour },
          min: 0,
        },
      ],
      tooltip: {
        shared: true,
        theme: mode,
        x: { formatter: (value: number) => `${value.toFixed(2)} h` },
        // Every rate point IS one slice, so the slice's own volume rides along with its rate. That answers
        // "how much paste went into that metre" without a third axis competing for room on the plot.
        y: {
          formatter: (value: number, opts?: { seriesIndex?: number; dataPointIndex?: number }) => {
            if (value === undefined || value === null) return '';

            // The height series sits after every band, and is a length rather than a rate.
            const seriesIndex = opts?.seriesIndex ?? -1;
            if (seriesIndex < 0 || seriesIndex >= bands.length) return `${value.toFixed(1)} ${lengthUnit}`;

            const rated = ratedSlicesByBand[seriesIndex] ?? [];
            // The closing point repeats the last slice's rate, so it reports that slice's volume too.
            const slice = rated[Math.min(opts?.dataPointIndex ?? 0, rated.length - 1)];
            const rate = `${value.toFixed(2)} ${lengthUnit}/h`;
            return slice ? `${rate} · ${formatSliceVolume(toVolume(slice.volume))} ${volumeUnit}` : rate;
          },
        },
      },
    };
  }, [chartSeries, bands, ratedSlicesByBand, series, lengthUnit, volumeUnit, isImperialVolume, axisColour, gridColour, mode, chartId]);

  if (series.slices.length === 0) {
    return (
      <Box sx={{ px: 1, py: 2 }}>
        <Typography variant="caption" color="text.secondary">
          <Trans id="No pours to plot a rise rate for yet.">No pours to plot a rise rate for yet.</Trans>
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 0.5, pb: 0.5 }}>
      <ReactApexChart options={options} series={chartSeries} type="line" height={height} />
      <Box sx={{ px: 1, pb: 0.5, display: hideCaptions ? 'none' : 'block' }}>
        {series.peakRiseRate !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
            {`${i18n._('Peak')}: ${toLength(series.peakRiseRate).toFixed(2)} ${lengthUnit}/h`}
            {` · ${series.totalHours.toFixed(1)} h`}
          </Typography>
        )}
        {series.hasUnknownFlowRate && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', lineHeight: 1.3 }}>
            <Trans id="Pours with no recipe flow rate (including rock fill) raise the fill but are not on the time axis.">
              Pours with no recipe flow rate (including rock fill) raise the fill but are not on the time axis.
            </Trans>
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
          <Trans id="Assumes a level fill surface, so heights here may differ slightly from the volume table.">
            Assumes a level fill surface, so heights here may differ slightly from the volume table.
          </Trans>
        </Typography>
      </Box>
    </Box>
  );
};

export default RiseRateChart;
